import type Dropdown from '../dropdown/Dropdown.js'
import type { OverflowListItem } from './OverflowList.js'
import type OverflowList from './OverflowList.js'
import { html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import './OverflowList.js'
import './OverflowListCount.js'
import '../button/Button.js'
import '../dropdown/Dropdown.js'
import '../dropdown-item/DropdownItem.js'

export type OverflowListMenuSize = 's' | 'm' | 'l'

/**
 * Provides a declarative menu for actions hidden by the nearest Overflow List.
 *
 * @status draft
 * @category layout
 * @parent OverflowList
 * @lightDom true
 *
 * @localization itemSingular - Default noun used when exactly one item is hidden.
 * @localization itemPlural - Default noun used when multiple items are hidden.
 * @localization showMore - Toggle label, given a locale-formatted count and item noun.
 * @localization fallbackItem - Fallback proxy label, given the item noun and one-based index.
 */
@customElement('nord-overflow-list-menu')
export default class OverflowListMenu extends DraftComponentMixin(LitElement) {
  /** Size passed to the owned Nord Dropdown and Button. */
  @property({ reflect: true }) size: OverflowListMenuSize = 'm'

  /** Shows only the signed count inside the owned toggle. */
  @property({ reflect: true, type: Boolean }) compact = false

  /** Noun used when exactly one item is hidden. */
  @property({ attribute: 'singular-label' }) singularLabel?: string

  /** Noun used when multiple items are hidden. */
  @property({ attribute: 'plural-label' }) pluralLabel?: string

  @state() private overflowItems: readonly OverflowListItem[] = []
  private list?: OverflowList
  private itemObserver?: MutationObserver
  private focusedProxyOriginal?: HTMLElement
  private localize = new LocalizeController<'nord-overflow-list-menu'>(this)

  connectedCallback() {
    this.slot = 'overflow'
    super.connectedCallback()
    this.addEventListener('click', this.handleProxyClick)
    this.addEventListener('focusin', this.handleFocusIn)
    this.addEventListener('focusout', this.handleFocusOut)
    this.bindList()
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleProxyClick)
    this.removeEventListener('focusin', this.handleFocusIn)
    this.removeEventListener('focusout', this.handleFocusOut)
    this.unbindList()
    super.disconnectedCallback()
  }

  protected createRenderRoot() {
    return this
  }

  protected render() {
    const count = this.overflowItems.length
    const formattedCount = this.formatCount(count)
    const itemLabel = count === 1
      ? this.singularLabel?.trim() || this.localize.term('itemSingular')
      : this.pluralLabel?.trim() || this.localize.term('itemPlural')
    const toggleLabel = this.localize.term('showMore', formattedCount, itemLabel)

    return html`
      <nord-dropdown .size=${this.size}>
        <nord-button
          slot="toggle"
          type="button"
          variant="plain"
          .size=${this.size}
          aria-label=${toggleLabel}
          ?hide-dropdown-icon=${this.compact}
          ?square=${this.compact}
        >
          <nord-overflow-list-count
            ?compact=${this.compact}
            .singularLabel=${this.singularLabel}
            .pluralLabel=${this.pluralLabel}
            .lang=${this.lang}
          ></nord-overflow-list-count>
        </nord-button>
        ${this.overflowItems.map(item => html`
          <nord-dropdown-item ?disabled=${this.isDisabled(item.element)}>
            ${this.itemLabel(item)}
          </nord-dropdown-item>
        `)}
      </nord-dropdown>
    `
  }

  private bindList() {
    const list = this.closest<OverflowList>('nord-overflow-list') ?? undefined
    if (list === this.list) {
      return
    }
    this.unbindList()
    this.list = list
    this.list?.addEventListener('nord-overflow-change', this.handleOverflowChange)
    this.setOverflowItems(this.list?.overflowItems ?? [])
  }

  private unbindList() {
    this.list?.removeEventListener('nord-overflow-change', this.handleOverflowChange)
    this.itemObserver?.disconnect()
    this.list = undefined
    this.overflowItems = []
  }

  private handleOverflowChange = (event: Event) => {
    if (event.target !== this.list) {
      return
    }
    const items = (event as Event & { overflowItems?: readonly OverflowListItem[] }).overflowItems
    this.setOverflowItems(items ?? this.list.overflowItems)
  }

  private setOverflowItems(items: readonly OverflowListItem[]) {
    if (!this.sameItems(this.overflowItems, items)) {
      this.restoreFocusBeforeItemsChange(items)
    }
    this.itemObserver?.disconnect()
    this.itemObserver ??= typeof MutationObserver === 'undefined'
      ? undefined
      : new MutationObserver(() => this.requestUpdate())
    this.overflowItems = items
    for (const { element } of items) {
      this.itemObserver?.observe(element, {
        attributeFilter: ['aria-disabled', 'aria-label', 'disabled'],
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true,
      })
    }
  }

  private sameItems(current: readonly OverflowListItem[], next: readonly OverflowListItem[]) {
    return current.length === next.length
      && current.every((item, index) => item.element === next[index]?.element && item.index === next[index]?.index)
  }

  private handleFocusIn = (event: FocusEvent) => {
    const proxy = event.composedPath().find(
      target => this.isHTMLElement(target) && target.localName === 'nord-dropdown-item',
    )
    if (!this.isHTMLElement(proxy) || !this.composedContains(this, proxy)) {
      return
    }
    const proxies = Array.from(this.querySelectorAll(':scope > nord-dropdown > nord-dropdown-item'))
    this.focusedProxyOriginal = this.overflowItems[proxies.indexOf(proxy)]?.element
  }

  private handleFocusOut = () => {
    queueMicrotask(() => {
      const activeElement = this.deepActiveElement()
      if (!activeElement || !this.composedContains(this, activeElement)) {
        this.focusedProxyOriginal = undefined
      }
    })
  }

  private itemLabel(item: OverflowListItem) {
    const explicit = item.element.getAttribute('aria-label')?.trim()
    const text = item.element.textContent?.replace(/\s+/g, ' ').trim()
    if (explicit || text) {
      return explicit || text
    }
    const itemLabel = this.singularLabel?.trim() || this.localize.term('itemSingular')
    return this.localize.term('fallbackItem', itemLabel, this.formatCount(item.index + 1))
  }

  private isDisabled(element: HTMLElement) {
    return (element as HTMLElement & { disabled?: boolean }).disabled === true
      || element.hasAttribute('disabled')
      || element.getAttribute('aria-disabled') === 'true'
  }

  private handleProxyClick = (event: MouseEvent) => {
    const proxy = event.composedPath().find(
      target => this.isHTMLElement(target) && target.localName === 'nord-dropdown-item',
    )
    if (!this.isHTMLElement(proxy) || !this.composedContains(this, proxy)) {
      return
    }
    event.stopPropagation()
    const proxies = Array.from(this.querySelectorAll(':scope > nord-dropdown > nord-dropdown-item'))
    const item = this.overflowItems[proxies.indexOf(proxy)]
    if (item && !this.isDisabled(item.element)) {
      this.closeMenuAndFocusToggle()
      item.element.click()
    }
  }

  private restoreFocusBeforeItemsChange(nextItems: readonly OverflowListItem[]) {
    const dropdown = this.querySelector<Dropdown>(':scope > nord-dropdown')
    const wasOpen = dropdown?.open === true
    const activeElement = this.deepActiveElement()
    const focusIsInsideMenu = activeElement ? this.composedContains(this, activeElement) : false
    if (!wasOpen && !focusIsInsideMenu) {
      this.focusedProxyOriginal = undefined
      return
    }

    if (wasOpen) {
      void dropdown.hide(false)
    }

    if (nextItems.length > 0 && focusIsInsideMenu) {
      dropdown?.querySelector<HTMLElement>(':scope > [slot="toggle"]')?.focus({ preventScroll: true })
    }
    else if (focusIsInsideMenu) {
      this.focusVisibleOriginal(this.focusedProxyOriginal)
    }
    this.focusedProxyOriginal = undefined
  }

  private closeMenuAndFocusToggle(
    dropdown = this.querySelector<Dropdown>(':scope > nord-dropdown'),
  ) {
    void dropdown?.hide(false)
    dropdown?.querySelector<HTMLElement>(':scope > [slot="toggle"]')?.focus({ preventScroll: true })
  }

  private focusVisibleOriginal(preferred?: HTMLElement) {
    const visibleElements = this.list?.visibleItems.map(item => item.element) ?? []
    const candidates = preferred && visibleElements.includes(preferred)
      ? [preferred, ...visibleElements.filter(element => element !== preferred)]
      : visibleElements

    for (const candidate of candidates) {
      candidate.focus({ preventScroll: true })
      const activeElement = this.deepActiveElement()
      if (activeElement && this.composedContains(candidate, activeElement)) {
        return
      }
      const descendant = candidate.querySelector<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      descendant?.focus({ preventScroll: true })
      const descendantActiveElement = this.deepActiveElement()
      if (descendantActiveElement && this.composedContains(candidate, descendantActiveElement)) {
        return
      }
    }
  }

  private deepActiveElement(): HTMLElement | undefined {
    const root = this.getRootNode()
    let activeElement = 'activeElement' in root
      ? (root as Document | ShadowRoot).activeElement
      : this.ownerDocument.activeElement
    while (this.isHTMLElement(activeElement) && activeElement.shadowRoot?.activeElement) {
      activeElement = activeElement.shadowRoot.activeElement
    }
    return this.isHTMLElement(activeElement) ? activeElement : undefined
  }

  private composedContains(container: HTMLElement, element: HTMLElement): boolean {
    let current: Node | null = element
    while (current) {
      if (current === container) {
        return true
      }
      const root = current.getRootNode()
      current = current.parentNode ?? ('host' in root ? (root as ShadowRoot).host : null)
    }
    return false
  }

  private isHTMLElement(value: unknown): value is HTMLElement {
    const HTMLElementConstructor = this.ownerDocument.defaultView?.HTMLElement
    return Boolean(HTMLElementConstructor && value instanceof HTMLElementConstructor)
  }

  private formatCount(count: number) {
    const locale = this.localize.lang || this.localize.resolvedLang || 'en-US'
    try {
      return new Intl.NumberFormat(locale).format(count)
    }
    catch {
      return new Intl.NumberFormat('en-US').format(count)
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-overflow-list-menu': OverflowListMenu
  }
}
